import {
  type IncludeArr,
  isExpanded,
  Organization,
  type OrganizationEntity,
  type OrganizationListParams,
} from "~/src/api";
import { type QueryClient, queryOptions } from "@tanstack/vue-query";
import { useCacheTimings } from "~/composables/cacheTiming";
import { toUnordered } from "~/utils/tanstack/global";

export const cacheOrganization = (
  client: () => QueryClient,
  organization: OrganizationEntity,
  authed = false,
) => {
  const qc = client();
  const expandedRelations = new Set(
    organization.relationships.filter(isExpanded).map((rel) => rel.type),
  );

  qc.setQueryData(
    [
      "organization",
      "entity",
      organization.id,
      {
        includes: toUnordered("includes", Array.from(expandedRelations)),
        authed: authed || undefined,
      },
    ],
    organization,
  );
};

export const useOrganizationList = (
  params: MaybeRefOrGetter<OrganizationListParams> = {},
  // auth?: AuthFunction,
) => {
  const unorderedParams = computed(() => {
    const paramsValue = toValue(params);
    return {
      ...paramsValue,
      includes: paramsValue.includes
        ? toUnordered("includes", paramsValue.includes)
        : undefined,
      focusedLanguages: paramsValue.focusedLanguages
        ? toUnordered("langs", paramsValue.focusedLanguages)
        : undefined,
      ids: paramsValue.ids ? toUnordered("ids", paramsValue.ids) : undefined,
      // authed: !!auth || undefined,
    };
  });

  return queryOptions({
    queryKey: ["organization", "collection", unorderedParams],
    queryFn: async ({ client }) => {
      const paramsValue = toValue(params);
      const res = await Organization.search(
        paramsValue,
        // (await auth?.()) || undefined,
      );
      // cache for individual entities returned by the list endpoint
      res.data.forEach((organization) =>
        cacheOrganization(() => client as QueryClient, organization),
      );
      return res;
    },
    staleTime: useCacheTimings().collection.organization,
  });
};

export const useOrganizationGet = (
  id: MaybeRefOrGetter<string>,
  includes: IncludeArr<OrganizationEntity> = [],
  // auth?: AuthFunction,
) => {
  return queryOptions({
    queryKey: [
      "organization",
      "entity",
      id,
      {
        includes: toUnordered("includes", includes),
        // authed: !!auth || undefined,
      },
    ],
    queryFn: async () => {
      const idValue = toValue(id);
      return await Organization.get(
        idValue,
        includes,
        // (await auth?.()) || undefined
      );
    },
    staleTime: useCacheTimings().entity.organization,
  });
};

export const useOrganizationGetBySlug = (
  slug: MaybeRefOrGetter<string>,
  includes: IncludeArr<OrganizationEntity> = [],
  // auth?: AuthFunction,
) => {
  return queryOptions({
    queryKey: [
      "organization",
      "entity",
      slug,
      {
        includes: toUnordered("includes", includes),
        // authed: !!auth || undefined,
      },
    ],
    queryFn: async () => {
      const slugValue = toValue(slug);
      return await Organization.getBySlug(
        slugValue,
        includes,
        // (await auth?.()) || undefined
      );
    },
    staleTime: useCacheTimings().entity.organization,
  });
};
